import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import api, { resetSessionExpiredFlag, resetLicenseBlockedFlag } from '../utils/api';
import { setDecimalConfig } from '../utils/formatCurrency';
import { clearReferenceCache } from '../utils/referenceCache';
import { resetHandles as resetPosOfflineHandles } from '../utils/posOffline';

const AuthContext = createContext(null);

const normalizeLaunchPage = (value) => {
  const v = String(value || '').trim().toLowerCase();
  if (v === 'user_based') return 'user_based';
  // A published Q AI dashboard assigned as the landing page: 'qai:<id>'.
  const m = v.match(/^qai:(\d+)$/);
  if (m) return `qai:${m[1]}`;
  return 'company_overview';
};

const normalizeDefaultPeriod = (value) => {
  const v = String(value || '').trim().toLowerCase();
  return (v === 'today' || v === 'ytd') ? v : 'mtd';
};

const normalizeUser = (input) => {
  if (!input) return null;
  return {
    ...input,
    launch_page: normalizeLaunchPage(input.launch_page),
    crm_dashboard: String(input.crm_dashboard || '').trim().toLowerCase() === 'sales_exec' ? 'sales_exec' : 'manager',
    default_period: normalizeDefaultPeriod(input.default_period),
    // Tri-state on purpose: true/false when the tenant has run migration 230,
    // undefined when it has not. WelcomeTour only treats an explicit `true` as
    // "already seen" and otherwise defers to the localStorage flag, so a
    // pre-migration tenant must not be flattened to false here.
    has_seen_welcome_tour: input.has_seen_welcome_tour === undefined
      ? undefined
      : Boolean(input.has_seen_welcome_tour),
  };
};

// Scoped the same way as every other per-user "seen" flag in the app
// (Customer360's zero-fireworks, ThemeContext's mode): one process serves many
// tenants and many users share a browser, so a bare key would suppress the tour
// for the next person who signs in here.
export const welcomeTourSeenKey = (user) => {
  if (!user?.id) return null;
  return `journey.welcomeTour.seen:${user.company_code || user.tenantDb || 'default'}:${user.id}`;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

export const useDecimalFormat = () => {
  const { decimalFormat } = useAuth();
  return decimalFormat || { qty: 2, price: 2 };
};

const PERMISSIONS_STALE_MS = 5 * 60 * 1000; // 5 minutes

// Last-known RBAC state, cached so a till that reloads during a network outage
// keeps rendering the controls the cashier had a moment earlier instead of a
// fully-denied screen — `can()` returns false for every key when the snapshot
// is null, and resetting the access lists to null means UNRESTRICTED (see
// filterWarehouses), so an outage would otherwise both blank the UI and widen
// warehouse access at the same time.
//
// SAFE BY CONSTRUCTION: this cache only drives what the UI renders. Every
// endpoint re-checks the real permission server-side on every request
// (server/middleware/requirePermission.js), so a stale cached grant cannot
// authorise anything — at worst it renders a button whose request is refused.
// It is cleared the moment the server actively rejects us.
const RBAC_CACHE_KEY = 'rbac.snapshot';

const readRbacCache = () => {
  try {
    const raw = localStorage.getItem(RBAC_CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const writeRbacCache = (patch) => {
  try {
    localStorage.setItem(RBAC_CACHE_KEY, JSON.stringify({ ...(readRbacCache() || {}), ...patch }));
  } catch {
    // Quota or private mode. The cache is an optimisation, never a requirement.
  }
};

const clearRbacCache = () => {
  try {
    localStorage.removeItem(RBAC_CACHE_KEY);
  } catch {
    // ignore
  }
};

/**
 * True when a request failed without the server answering at all.
 *
 * axios sets `response` only when a reply arrived. "The server rejected this"
 * and "there is no server right now" are different facts that must not share a
 * remedy — conflating them is what logs a cashier out of a till mid-outage.
 */
const isTransportFailure = (err) => !err?.response;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [permissionSnapshot, setPermissionSnapshot] = useState(null);
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  const permissionsLoadedAtRef = useRef(0);
  const [allowedWarehouseIds, setAllowedWarehouseIds] = useState(null);
  const [allowedLocationIds, setAllowedLocationIds] = useState(null);
  // { document_type: warehouse_id } — which warehouse each transaction form
  // pre-selects for this user (Settings → Users & Roles → Location & Warehouse
  // Access). Empty object, not null, is the "no defaults" state: unlike the
  // access lists above, absent means "pick nothing", never "unrestricted".
  const [defaultWarehouses, setDefaultWarehouses] = useState({});
  const [isAiEnabled, setIsAiEnabled] = useState(true); // optimistic default
  // Pessimistic, deliberately the opposite of isAiEnabled above. AI is on for
  // everyone, so optimism there avoids a flash of a missing button. The feedback
  // channel is governed by JourneyPlatformDB and is OFF until that database says
  // otherwise, so a deployment where the platform migration has not been applied
  // must never flash the button in and then take it away.
  const [isFeedbackEnabled, setIsFeedbackEnabled] = useState(false);
  const [decimalFormat, setDecimalFormat] = useState({ qty: 2, price: 2 });
  const [sessionExpired, setSessionExpired] = useState(false);
  // Tenant licence state. Deliberately NOT folded into `user`: normalizeUser
  // persists `user` to localStorage, and a days_remaining cached from last week
  // would render a stale countdown on every page load before /auth/me returns.
  const [license, setLicense] = useState(null);
  const [licenseBlock, setLicenseBlock] = useState(null);

  const loadAccessLists = useCallback(async () => {
    if (!localStorage.getItem('token')) {
      setAllowedWarehouseIds(null);
      setAllowedLocationIds(null);
      setDefaultWarehouses({});
      return;
    }
    try {
      const [whRes, locRes, settingsRes, feedbackRes, defWhRes] = await Promise.all([
        api.get('/rbac/me/warehouse-access'),
        api.get('/rbac/me/location-access'),
        api.get('/settings'),
        // Carries its OWN catch rather than relying on the shared one below:
        // that catch resets the access lists to null, and null means
        // UNRESTRICTED (see filterWarehouses). Without this, a hiccup on the
        // newest and least important of the four endpoints would silently widen
        // every user's warehouse and location access.
        api.get('/feedback/config').catch(() => ({ data: { enabled: false } })),
        // Own catch, same reasoning as /feedback/config: a preference must never
        // be able to fail the request that carries the access lists.
        api.get('/rbac/me/default-warehouses').catch(() => ({ data: { default_warehouses: {} } })),
      ]);
      setAllowedWarehouseIds(whRes.data?.allowed_warehouse_ids ?? null);
      setAllowedLocationIds(locRes.data?.allowed_location_ids ?? null);
      const nextDefaultWarehouses = defWhRes.data?.default_warehouses || {};
      setDefaultWarehouses(nextDefaultWarehouses);
      // === true, not truthy: a proxy that answers with an HTML error page must
      // not read as "enabled".
      setIsFeedbackEnabled(feedbackRes.data?.enabled === true);
      const allSettings = settingsRes.data || {};
      setIsAiEnabled(allSettings['ai.enabled'] !== 'false');

      const qtyDp = parseInt(allSettings['format.quantity_decimal_places'], 10);
      const priceDp = parseInt(allSettings['format.price_decimal_places'], 10);
      const newDecimalFormat = {
        qty: Number.isFinite(qtyDp) && qtyDp >= 0 ? qtyDp : 2,
        price: Number.isFinite(priceDp) && priceDp >= 0 ? priceDp : 2,
      };
      setDecimalFormat(newDecimalFormat);
      setDecimalConfig(newDecimalFormat);
      writeRbacCache({
        allowedWarehouseIds: whRes.data?.allowed_warehouse_ids ?? null,
        allowedLocationIds: locRes.data?.allowed_location_ids ?? null,
        defaultWarehouses: nextDefaultWarehouses,
        decimalFormat: newDecimalFormat,
      });
    } catch (err) {
      // Offline: hold the last-known lists. Falling through to null would read
      // as UNRESTRICTED and silently widen every user's warehouse and location
      // access for the duration of the outage.
      if (isTransportFailure(err)) {
        const cached = readRbacCache();
        if (cached && Object.prototype.hasOwnProperty.call(cached, 'allowedWarehouseIds')) {
          setAllowedWarehouseIds(cached.allowedWarehouseIds);
          setAllowedLocationIds(cached.allowedLocationIds ?? null);
          setDefaultWarehouses(cached.defaultWarehouses || {});
          if (cached.decimalFormat) {
            setDecimalFormat(cached.decimalFormat);
            setDecimalConfig(cached.decimalFormat);
          }
          return;
        }
      }
      setAllowedWarehouseIds(null);
      setAllowedLocationIds(null);
      setDefaultWarehouses({});
    }
  }, []);

  const loadEffectivePermissions = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setPermissionSnapshot(null);
      return null;
    }

    setPermissionsLoading(true);
    try {
      const res = await api.get('/rbac/me/effective');
      permissionsLoadedAtRef.current = Date.now();
      setPermissionSnapshot(res.data || null);
      writeRbacCache({ permissionSnapshot: res.data || null });
      return res.data || null;
    } catch (err) {
      // Offline: fall back to the last-known snapshot. Nulling it here would
      // make can() deny every permission key for non-admins, so a till that
      // reloaded mid-outage would render with every control stripped out.
      if (isTransportFailure(err)) {
        const cached = readRbacCache();
        if (cached?.permissionSnapshot) {
          setPermissionSnapshot(cached.permissionSnapshot);
          return cached.permissionSnapshot;
        }
      }
      setPermissionSnapshot(null);
      return null;
    } finally {
      setPermissionsLoading(false);
    }
  }, []);

  useEffect(() => {
    const bootstrap = async () => {
      const token = localStorage.getItem('token');
      const savedUser = localStorage.getItem('user');
      if (token && savedUser) {
        try {
          const normalized = normalizeUser(JSON.parse(savedUser));
          localStorage.setItem('user', JSON.stringify(normalized));
          setUser(normalized);
          const [meRes] = await Promise.all([
            api.get('/auth/me'),
            loadEffectivePermissions(),
            loadAccessLists(),
          ]);
          if (meRes?.data) {
            // Preserve tenantDb from the stored user if /auth/me doesn't return it
            // (e.g. in dev/single-tenant mode where the platform DB is not set up).
            //
            // company_code and company_name are preserved the same way and for the
            // same reason: both are tenant-level and immutable, /auth/login is the
            // only endpoint that returns them, and without this a page reload would
            // drop the code the user needs to see in Settings.
            const fresh = normalizeUser({
              tenantDb: normalized?.tenantDb || null,
              company_code: normalized?.company_code || null,
              company_name: normalized?.company_name || null,
              // Same lifecycle as company_code: tenant-level, immutable for the
              // life of the tenant, returned only by /auth/login. AIChat keys
              // browser-local state by it, so dropping it on reload would silently
              // fall back to the reusable db name and reopen the cross-tenant
              // transcript replay it exists to prevent.
              tenant_registry_id: normalized?.tenant_registry_id ?? null,
              ...meRes.data,
            });
            localStorage.setItem('user', JSON.stringify(fresh));
            setUser(fresh);
            setLicense(meRes.data.license || null);
          }
        } catch (err) {
          // Only the /auth/me call can land here — loadEffectivePermissions and
          // loadAccessLists both swallow their own failures — so `err` is that
          // request's error and its shape is meaningful.
          //
          // Clearing credentials on a TRANSPORT failure is a lockout: the till
          // has no network, so the cashier cannot sign back in, and the offline
          // sales already queued on the device become unreachable behind a login
          // screen that can never be satisfied. Only a real answer from the
          // server (401 token rejected, 402 licence lapsed) justifies eviction.
          if (!isTransportFailure(err)) {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            clearRbacCache();
            setPermissionSnapshot(null);
          }
          // Offline: keep the stored session. `user` is already set from
          // localStorage above and loadEffectivePermissions has restored the
          // cached RBAC snapshot, so the app carries on with last-known identity
          // and rights until the network returns and /auth/me can be re-checked.
        }
      }
      setLoading(false);
    };

    bootstrap();
  }, [loadEffectivePermissions, loadAccessLists]);

  // Listen for the session-expired event dispatched by the api interceptor.
  // We show a re-login modal overlay instead of hard-redirecting so that
  // any unsaved form data on the current page is preserved.
  useEffect(() => {
    const handler = () => setSessionExpired(true);
    window.addEventListener('journey:session-expired', handler);
    return () => window.removeEventListener('journey:session-expired', handler);
  }, []);

  // The tenant's licence lapsed or was suspended (402 from the api interceptor).
  // Distinct from sessionExpired: there is nothing the user can do to clear it,
  // so the modal it drives offers no re-login — only the reason and a sign-out.
  useEffect(() => {
    const handler = (e) => setLicenseBlock(e.detail || { reason: 'expired' });
    window.addEventListener('journey:license-expired', handler);
    return () => window.removeEventListener('journey:license-expired', handler);
  }, []);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      if (!localStorage.getItem('token')) return;
      if (Date.now() - permissionsLoadedAtRef.current > PERMISSIONS_STALE_MS) {
        loadEffectivePermissions();
        loadAccessLists();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [loadEffectivePermissions, loadAccessLists]);

  // companyCode is OPTIONAL. An email address belongs to exactly one company, so
  // it routes on its own; the code is how a user names the company explicitly —
  // needed only when their registry row is missing, and useful as a check that
  // they are signing in to the company they think they are.
  const login = useCallback(async (email, password, companyCode) => {
    const res = await api.post('/auth/login', {
      email,
      password,
      // Omitted rather than sent empty: the server validates the field whenever it
      // is present, so '' would be rejected as a malformed code.
      ...(companyCode ? { company_code: companyCode } : {}),
    });
    const { token, user: userData } = res.data;
    const normalized = normalizeUser(userData);
    // Drop any reference data cached under a previous session/tenant so this login
    // never serves another tenant's customers/items/etc.
    clearReferenceCache();
    // Same reason: the cached RBAC snapshot belongs to whoever was signed in
    // before. It must be gone before this login's permissions load, or a moment
    // of the previous user's rights would render against the new identity.
    clearRbacCache();
    localStorage.setItem('token', token);
    // Release POS offline handles AFTER the new token is stored: the database
    // name is derived from the token's tenantDb claim, so the next call must
    // resolve against the tenant that was just signed into, not the previous
    // one. Queued sales are namespaced and survive this.
    resetPosOfflineHandles();
    localStorage.setItem('user', JSON.stringify(normalized));
    setUser(normalized);
    setLicense(res.data.license || null);
    setSessionExpired(false);
    resetSessionExpiredFlag();
    setLicenseBlock(null);
    resetLicenseBlockedFlag();
    // Fire permissions/access-list load in the background — do not await.
    // Awaiting here lets a 401 from those secondary calls hit the global
    // interceptor before the caller has had a chance to navigate away from /login,
    // which clears the token and sends the user back to the sign-in page.
    Promise.all([loadEffectivePermissions(), loadAccessLists()]).catch(() => {});
    return normalized;
  }, [loadEffectivePermissions, loadAccessLists]);

  const updateStoredUser = useCallback((nextUser) => {
    if (!nextUser) return;
    const normalized = normalizeUser(nextUser);
    localStorage.setItem('user', JSON.stringify(normalized));
    setUser(normalized);
  }, []);

  // Records that this user has been through the first-run welcome tour.
  // Writes locally FIRST and never awaits the server: the tour is already
  // closing by the time this runs, and a failed round-trip must not be able to
  // resurrect it on the next page load.
  const markWelcomeTourSeen = useCallback(() => {
    setUser((prev) => {
      if (!prev) return prev;
      const key = welcomeTourSeenKey(prev);
      if (key) {
        try {
          localStorage.setItem(key, new Date().toISOString());
        } catch {
          // Private-mode / quota failures fall through to the server flag.
        }
      }
      const next = normalizeUser({ ...prev, has_seen_welcome_tour: true });
      try {
        localStorage.setItem('user', JSON.stringify(next));
      } catch {
        // Non-fatal; the in-memory user below is what this session renders from.
      }
      return next;
    });
    api.post('/auth/me/welcome-tour-seen').catch(() => {});
  }, []);

  const changePassword = useCallback(async (currentPassword, newPassword) => {
    const res = await api.post('/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
    });
    // The server evicts every session issued before the change — including the
    // token this request was made with — and returns a replacement. Without
    // storing it, the very next call 401s and drops the user into re-login.
    if (res.data?.token) {
      localStorage.setItem('token', res.data.token);
    }
    const existing = JSON.parse(localStorage.getItem('user') || '{}');
    const updatedUser = {
      ...existing,
      ...(res.data?.user || {}),
      must_change_password: false,
    };
    updateStoredUser(updatedUser);
    return res.data;
  }, [updateStoredUser]);

  const logout = useCallback(() => {
    // Tell the server the session is over so it stops appearing as live under
    // Settings > Audit Log > User Sessions, and so the discarded token cannot
    // be replayed. Fire-and-forget on purpose: signing out must not be blocked
    // by, or fail because of, the network — the local state is cleared below
    // either way. Sent before the token is removed, since the call needs it.
    if (localStorage.getItem('token')) {
      api.post('/auth/logout').catch(() => { /* leaving anyway */ });
    }
    clearReferenceCache();
    resetPosOfflineHandles();
    clearRbacCache();
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    permissionsLoadedAtRef.current = 0;
    setUser(null);
    setPermissionSnapshot(null);
    setAllowedWarehouseIds(null);
    setAllowedLocationIds(null);
    setIsAiEnabled(true);
    setIsFeedbackEnabled(false);
    setDecimalFormat({ qty: 2, price: 2 });
    setDecimalConfig({ qty: 2, price: 2 });
    setSessionExpired(false);
    resetSessionExpiredFlag();
    setLicense(null);
    setLicenseBlock(null);
    resetLicenseBlockedFlag();
  }, []);

  const can = useCallback((permissionKey) => {
    if (!permissionKey) return true;
    // The system admin holds everything — checked BEFORE the snapshot so a stale
    // or explicitly-denying entry cannot outrank being an administrator. Mirrors
    // rbacModel.hasPermission; the snapshot itself already resolves to all-true
    // for an admin, so this only matters before the first refresh lands.
    if (String(user?.role || '').trim().toLowerCase() === 'admin') return true;
    const permissions = permissionSnapshot?.permissions || {};
    if (Object.prototype.hasOwnProperty.call(permissions, permissionKey)) {
      return !!permissions[permissionKey];
    }
    return false;
  }, [permissionSnapshot, user]);

  // Navbar leaf visibility: hidden only when explicitly denied (=== false).
  // Absent keys default to shown, independent of the admin default-true in `can`.
  const isNavHidden = useCallback((navKey) => {
    if (!navKey) return false;
    if (String(user?.role || '').trim().toLowerCase() === 'admin') return false;
    const permissions = permissionSnapshot?.permissions || {};
    return permissions[navKey] === false;
  }, [permissionSnapshot, user]);

  const filterWarehouses = useCallback((list) => {
    if (!Array.isArray(list) || allowedWarehouseIds === null) return list;
    const ok = new Set(allowedWarehouseIds.map(Number));
    return list.filter((w) => ok.has(Number(w.id)));
  }, [allowedWarehouseIds]);

  const filterLocations = useCallback((list) => {
    if (!Array.isArray(list) || allowedLocationIds === null) return list;
    const ok = new Set(allowedLocationIds.map(Number));
    return list.filter((l) => ok.has(Number(l.id)));
  }, [allowedLocationIds]);

  /**
   * The warehouse a form should pre-select, as a string (form selects hold
   * strings) or '' when there is no usable default.
   *
   * Accepts several document types so a form that does not yet know which
   * document it is saving can express a preference order — SalesInvoiceForm on
   * a new document is Quote, Sales Order and Sales Invoice all at once until
   * the user picks a Save button, so it asks for its current status first and
   * falls back through the rest.
   *
   * `options` may carry the warehouse list actually rendered by the picker; the
   * default is dropped if it is not in there, so a warehouse the form has
   * already excluded (deleted, non-sales, not linked to the item) never gets
   * silently pre-selected.
   */
  const defaultWarehouseFor = useCallback((docTypes, options = null) => {
    const keys = Array.isArray(docTypes) ? docTypes : [docTypes];
    const available = Array.isArray(options)
      ? new Set(options.map((w) => Number(w?.id ?? w)))
      : null;
    for (const key of keys) {
      const warehouseId = key ? defaultWarehouses[key] : null;
      if (warehouseId === null || warehouseId === undefined || warehouseId === '') continue;
      if (available && !available.has(Number(warehouseId))) continue;
      return String(warehouseId);
    }
    return '';
  }, [defaultWarehouses]);

  const value = {
    user,
    loading,
    permissionsLoading,
    permissionSnapshot,
    isAuthenticated: !!user,
    isAiEnabled,
    isFeedbackEnabled,
    decimalFormat,
    sessionExpired,
    license,
    licenseBlock,
    login,
    changePassword,
    updateStoredUser,
    markWelcomeTourSeen,
    logout,
    can,
    isNavHidden,
    allowedWarehouseIds,
    allowedLocationIds,
    defaultWarehouses,
    filterWarehouses,
    filterLocations,
    defaultWarehouseFor,
    refreshPermissions: loadEffectivePermissions,
    refreshAccessLists: loadAccessLists,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
